// @ts-check
// panels.js — Sidebar panel pin/peek/cycle behaviour.
//
// Manages the three sidebar panels (Notes, Tasks, Schedule):
//   • applyPinState     — propagates `isPanelPinned` to body classes
//   • cyclePanel        — switches the visible panel (desktop)
//   • showPanel/scheduleHidePanel — peek-on-hover when unpinned
//
// Phase 7: all cross-domain access (panel-pin state, schedule date,
// SettingsRouter.close, renderSchedule) goes through typed imports;
// PanelHover is exported as named functions, no window.* shim.

import {
  getScheduleDate,
  renderSchedule,
  setScheduleDate,
  setScheduleNeedsScrollToNow,
} from '../schedule/schedule.js';
import { SettingsRouter } from '../settings/routing.js';
import { debounce } from '../utils/debounce.js';
import { mobileMediaQuery } from '../core/app-state.js';

// ── Panel domain state (was in core/app-state.js pre-L6) ─────────────────
// isPanelPinned mirrors the panel_pinned localStorage flag; peekHideTimer
// is the auto-hide timeout handle. Both lived in app-state.js for
// historical reasons; L6 relocates them to their domain owner.
let isPanelPinned = localStorage.getItem('panel_pinned') === 'true';
/** @returns {boolean} */
export function getIsPanelPinned() {
  return isPanelPinned;
}
/**
 * @param {boolean} v
 * @returns {void}
 */
function setIsPanelPinned(v: any) {
  isPanelPinned = v;
}

const panelLists = document.getElementById('panel-lists') as any;
const panelPin = document.getElementById('panel-pin') as any;
const panelOpenBtn = document.getElementById('panel-open-btn') as any;
const panelArrow = document.getElementById('panel-arrow') as any;
const filesContainer = document.getElementById('files-container') as any;
const todosContainer = document.getElementById('todo-container') as any;
const scheduleContainer = document.getElementById('schedule-container') as any;
const searchContainer = document.getElementById('search-container') as any;
const settingsContainer = document.getElementById('settings-container') as any;
const globalSearchPanel = document.getElementById('global-search-panel') as any;
const threadContainer = document.getElementById('thread-container') as any;
const threadPanel = document.getElementById('thread-panel') as any;

// Desktop: dock Find & Replace into the side panel as a cycle surface — move
// the floating #global-search-panel into #search-container. On mobile (coarse
// pointer) it stays a body-level bottom sheet and search is NOT in the cycle.
const _searchDocked = !mobileMediaQuery.matches;
if (_searchDocked && searchContainer && globalSearchPanel) {
  // The "Lens" markup (index.html) is already the final layout — an inline find
  // bar, a scope row, a replace field, then results — identical for the mobile
  // bottom sheet and the docked sidebar. So docking is just a reparent (no row
  // rebuild): move the live panel in and let the surface .active toggle drive
  // visibility. Docked-specific chrome (width, the ✕ hidden) is pure CSS.
  searchContainer.appendChild(globalSearchPanel);
  globalSearchPanel.classList.remove('gs-hidden');
}
// Desktop: dock the Labyrinth Thread walk into the side panel as an overlay
// surface — reparent the body-level #thread-panel into #thread-container (the
// #thread-container .active toggle now controls its visibility). On mobile
// (coarse pointer) it stays a body-level bottom sheet, exactly like Find & Replace.
const _threadDocked = !mobileMediaQuery.matches;
if (_threadDocked && threadContainer && threadPanel) {
  threadContainer.appendChild(threadPanel);
  threadPanel.classList.remove('thread-hidden');
}

const schedulePrevBtn = document.getElementById('schedule-prev') as any;
const scheduleNextBtn = document.getElementById('schedule-next') as any;
const scheduleDateLabel = document.getElementById('schedule-date-label') as any;

// ── Panel management ──────────────────────────────────────────────────────

/** @returns {void} */
function applyPinState() {
  if (getIsPanelPinned()) {
    panelLists.classList.add('pinned', 'visible');
    panelPin.classList.add('active');
    document.body.classList.add('panel-pinned');
  } else {
    panelLists.classList.remove('pinned', 'visible');
    panelPin.classList.remove('active');
    document.body.classList.remove('panel-pinned');
  }
}

// Suppresses hover-triggered panel open after unpinning until the cursor
// leaves the button area, but never blocks click-triggered opens.
let suppressHoverOpen = false;

panelPin.addEventListener('click', () => {
  const wasUnpinning = getIsPanelPinned();
  setIsPanelPinned(!getIsPanelPinned());
  localStorage.setItem('panel_pinned', String(getIsPanelPinned()));
  // When the panel stays visible while toggling pin state, suppress the
  // status pill's left-position transition so it snaps instantly rather than
  // sliding across to its new centred position.
  if (document.body.classList.contains('panel-visible')) {
    const pill = document.getElementById('bottom-status-area');
    if (pill) {
      pill.classList.add('no-position-transition');
      setTimeout(() => pill.classList.remove('no-position-transition'), 50);
    }
  }
  applyPinState();
  if (wasUnpinning) {
    // Suppress hover re-open until the mouse leaves the button area.
    // The button doesn't move while this listener is live (no resize,
    // no scroll), so cache the bounding rect once instead of calling
    // getBoundingClientRect on every mousemove — that call forces a
    // layout flush and adds up fast on a moving cursor.
    suppressHoverOpen = true;
    const rect = panelOpenBtn.getBoundingClientRect();
    const left = rect.left,
      right = rect.right,
      top = rect.top,
      bottom = rect.bottom;
    const onMouseMove = (/** @type {any} */ e: any) => {
      if (e.clientX < left || e.clientX > right || e.clientY < top || e.clientY > bottom) {
        suppressHoverOpen = false;
        document.removeEventListener('mousemove', onMouseMove);
      }
    };
    document.addEventListener('mousemove', onMouseMove);
  }
});

/** @returns {void} */
function cyclePanel() {
  // Settings is an overlay, not part of the cycle: dismiss it first so we
  // don't end up with two panels marked .active simultaneously.
  if (settingsContainer.classList.contains('active') && SettingsRouter?.close) {
    SettingsRouter.close();
    return;
  }
  // Thread is an overlay surface (like Find & Replace), not part of the rotation:
  // cycling out of it exits the walk (labyrinth-view owns the teardown + camera).
  if (threadContainer?.classList.contains('active')) {
    window.dispatchEvent(new CustomEvent('thread:exit'));
    return;
  }
  if (filesContainer.classList.contains('active')) {
    filesContainer.classList.remove('active');
    todosContainer.classList.add('active');
    localStorage.setItem('active_panel', 'tasks');
  } else if (todosContainer.classList.contains('active')) {
    todosContainer.classList.remove('active');
    scheduleContainer.classList.add('active');
    localStorage.setItem('active_panel', 'schedule');
    setScheduleNeedsScrollToNow(true);
    renderSchedule();
  } else if (scheduleContainer.classList.contains('active')) {
    // Schedule is the last panel in the cycle; wrap back to Notes. Find &
    // Replace is a docked surface but NOT part of the rotation — it's only
    // reachable via the find shortcut/action (openGlobalSearch).
    scheduleContainer.classList.remove('active');
    filesContainer.classList.add('active');
    localStorage.setItem('active_panel', 'files');
  } else {
    // Any unexpected state (incl. the search surface, should it ever be the
    // only .active panel) wraps back to Notes without joining search to the
    // cycle.
    searchContainer.classList.remove('active');
    filesContainer.classList.add('active');
    localStorage.setItem('active_panel', 'files');
  }
}

panelArrow.addEventListener('click', cyclePanel);

// Panel-header click + keyboard handling. Each h2 acts as a button; making
// it focusable and Enter/Space-activatable means keyboard users can switch
// panels without reaching for the panel-arrow control. role="button" tells
// assistive tech to announce it as such instead of as a heading.
/**
 * @param {Element | null} header
 * @param {() => void} onActivate
 * @returns {void}
 */
function _wirePanelHeader(header: any, onActivate: any) {
  if (!header) return;
  header.setAttribute('role', 'button');
  header.setAttribute('tabindex', '0');
  header.addEventListener('click', onActivate);
  header.addEventListener('keydown', (/** @type {any} */ e: any) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate();
    }
  });
}

_wirePanelHeader(filesContainer.querySelector('h2'), cyclePanel);
_wirePanelHeader(todosContainer.querySelector('h2'), cyclePanel);
_wirePanelHeader(scheduleContainer.querySelector('h2'), cyclePanel);
// The Find & Replace heading EXITS the surface — it does NOT cycle. This
// mirrors Settings, whose heading closes the panel (settings-panel.ts).
// deactivateSearchSurface restores whichever panel (Notes/Tasks/Schedule) was
// active before search opened. (deactivateSearchSurface is a hoisted function
// declaration defined below.)
if (searchContainer) _wirePanelHeader(searchContainer.querySelector('h2'), deactivateSearchSurface);
// The Thread heading EXITS the walk — it fires `thread:exit`, which labyrinth-view
// handles (reset state + zoom the map back out + deactivateThreadSurface). Routing
// it through the event keeps panels.ts unaware of the Labyrinth internals.
if (threadContainer)
  _wirePanelHeader(threadContainer.querySelector('h2'), () =>
    window.dispatchEvent(new CustomEvent('thread:exit'))
  );

schedulePrevBtn.addEventListener('click', () => {
  getScheduleDate().setDate(getScheduleDate().getDate() - 7);
  renderSchedule();
});
scheduleNextBtn.addEventListener('click', () => {
  getScheduleDate().setDate(getScheduleDate().getDate() + 7);
  renderSchedule();
});
scheduleDateLabel.addEventListener('click', () => {
  setScheduleDate(new Date());
  setScheduleNeedsScrollToNow(true);
  renderSchedule();
});

// force=true bypasses the hover-suppression flag (used for click opens)
/**
 * Open the panel (peek). `force=true` bypasses the hover-suppression flag
 * (used for click opens); hover listeners call `showPanel()` with no argument
 * so the default `false` lets the suppression guard run.
 * @param {boolean} [force]
 * @returns {void}
 */
function showPanel(force = false) {
  if (getIsPanelPinned()) return;
  if (suppressHoverOpen && !force) return;
  hidePanelSoon.cancel();
  panelLists.classList.add('visible');
  document.body.classList.add('panel-visible');
}

// Debounced auto-hide. 220ms is longer than the panel's 180ms slide-in
// (panels.css:190) so that mouseenter on panel-lists has time to fire after
// the panel has slid under the cursor; a shorter timer races the animation
// and the panel hides while the user's mouse is on it. The defensive :hover
// hit-test at fire time aborts the hide if the cursor is still over any
// panel-related element (the resizer/panel/button overlap by ~3px and the
// slide-in animation can delay mouseenter on panel-lists past the timer).
const hidePanelSoon = debounce(() => {
  if (panelLists.matches(':hover')) return;
  if (panelOpenBtn.matches(':hover')) return;
  const resizer = document.getElementById('panel-resizer');
  if (resizer?.matches(':hover')) return;
  panelLists.classList.remove('visible');
  document.body.classList.remove('panel-visible');
}, 220);

/** @returns {void} */
function scheduleHidePanel() {
  if (getIsPanelPinned()) return;
  // Don't auto-hide while the user is dragging the resize handle: the
  // cursor naturally crosses out of the panel area during a leftward
  // drag, and hiding mid-drag would yank the resizer out from under it.
  if (document.body.classList.contains('panel-resizing')) return;
  hidePanelSoon();
}

panelOpenBtn.addEventListener('mouseenter', () => showPanel());
panelOpenBtn.addEventListener('mouseleave', scheduleHidePanel);
panelOpenBtn.addEventListener('click', () => showPanel(true));
panelLists.addEventListener('mouseenter', () => showPanel());
panelLists.addEventListener('mouseleave', scheduleHidePanel);

applyPinState();

// Restore the last active panel
{
  const savedPanel = localStorage.getItem('active_panel');
  if (savedPanel === 'tasks') {
    filesContainer.classList.remove('active');
    todosContainer.classList.add('active');
  } else if (savedPanel === 'schedule') {
    filesContainer.classList.remove('active');
    scheduleContainer.classList.add('active');
    setScheduleNeedsScrollToNow(true);
    renderSchedule();
  }
  // Note: 'search' is intentionally NOT restorable. The docked Find & Replace
  // surface is no longer a cycle panel, so a stale active_panel='search' (from
  // a previous build) falls through to the default #files-container .active —
  // the persisted active panel can never restore to the search surface.
}

// Peek-hover helpers — used by ui/resizable-panel.js (the resize handle
// is a sibling element of the panel and needs to keep the panel open
// while the cursor hovers it) and by core/app-init.js. Both consumers
// import directly via the named exports below; the previous
// window.PanelHover shim is gone.

// ── Find & Replace surface (desktop docked search) ──────────────────────
// openGlobalSearch (global-search.js) calls these on non-mobile to show/hide
// the docked search surface. Find & Replace is NOT a cycle panel: it overlays
// the previously-active panel and, on close, restores it. We remember which
// of Notes/Tasks/Schedule was active so closing returns there (never to a
// hardcoded Notes), and we deliberately do NOT persist active_panel='search'
// so a reload can't restore into the search surface.
let _panelBeforeSearch: 'files' | 'tasks' | 'schedule' = 'files';
/** @returns {void} */
export function activateSearchSurface() {
  if (settingsContainer.classList.contains('active') && SettingsRouter?.close)
    SettingsRouter.close();
  // Remember the panel under the search surface so close() can restore it.
  // (If search is somehow already active, keep the remembered value.)
  if (todosContainer.classList.contains('active')) _panelBeforeSearch = 'tasks';
  else if (scheduleContainer.classList.contains('active')) _panelBeforeSearch = 'schedule';
  else if (filesContainer.classList.contains('active')) _panelBeforeSearch = 'files';
  filesContainer.classList.remove('active');
  todosContainer.classList.remove('active');
  scheduleContainer.classList.remove('active');
  threadContainer?.classList.remove('active'); // peer overlay — mutually exclusive with Find
  searchContainer.classList.add('active');
  // Do NOT persist active_panel='search': search is not a restorable cycle
  // panel. Leave active_panel pointing at the underlying panel.
  localStorage.setItem('active_panel', _panelBeforeSearch);
  if (!getIsPanelPinned()) showPanel();
}
/** @returns {void} */
export function deactivateSearchSurface() {
  searchContainer.classList.remove('active');
  const target =
    _panelBeforeSearch === 'tasks'
      ? todosContainer
      : _panelBeforeSearch === 'schedule'
        ? scheduleContainer
        : filesContainer;
  target.classList.add('active');
  localStorage.setItem('active_panel', _panelBeforeSearch);
  if (_panelBeforeSearch === 'schedule') {
    setScheduleNeedsScrollToNow(true);
    renderSchedule();
  }
}

// ── Thread surface (Labyrinth alias/merge/conflict walk) ────────────────────
// labyrinth-view.ts calls these to show/hide the walk. Like Find & Replace, Thread
// overlays the previously-active panel and restores it on close, and is never
// persisted as active_panel. On mobile it's a body-level bottom sheet instead of a
// cycle surface, so there's no underlying panel to remember.
let _panelBeforeThread: 'files' | 'tasks' | 'schedule' = 'files';
/** @returns {void} */
export function activateThreadSurface() {
  if (!_threadDocked) {
    // Mobile: body-level bottom sheet (mirrors the docked-search mobile path).
    threadPanel?.classList.remove('thread-hidden');
    return;
  }
  if (settingsContainer.classList.contains('active') && SettingsRouter?.close)
    SettingsRouter.close();
  if (todosContainer.classList.contains('active')) _panelBeforeThread = 'tasks';
  else if (scheduleContainer.classList.contains('active')) _panelBeforeThread = 'schedule';
  else if (filesContainer.classList.contains('active')) _panelBeforeThread = 'files';
  filesContainer.classList.remove('active');
  todosContainer.classList.remove('active');
  scheduleContainer.classList.remove('active');
  searchContainer?.classList.remove('active');
  threadContainer.classList.add('active');
  // Leave active_panel pointing at the underlying panel — Thread, like search, is
  // not a restorable cycle surface.
  localStorage.setItem('active_panel', _panelBeforeThread);
  if (!getIsPanelPinned()) showPanel();
}
/** @returns {void} */
export function deactivateThreadSurface() {
  if (!_threadDocked) {
    threadPanel?.classList.add('thread-hidden');
    return;
  }
  threadContainer.classList.remove('active');
  const target =
    _panelBeforeThread === 'tasks'
      ? todosContainer
      : _panelBeforeThread === 'schedule'
        ? scheduleContainer
        : filesContainer;
  target.classList.add('active');
  localStorage.setItem('active_panel', _panelBeforeThread);
  if (_panelBeforeThread === 'schedule') {
    setScheduleNeedsScrollToNow(true);
    renderSchedule();
  }
}

export { scheduleHidePanel, showPanel };
